import useSWR from 'swr';
import { fetchCurrentUser, ME_SWR_KEY, type CurrentUser } from '@/api/users';

interface UseCurrentUserResult {
  user: CurrentUser | null;
  isLoading: boolean;
  error: unknown;
  refresh: () => void;
}

// Last-known identity, persisted per browser. The sidebar's role gates hang
// off /me; when the API is slow or briefly down (backend deploys share the
// container with celery — heavy analytics requests starve the 8-slot worker
// pool), a cold page load rendered a stump with only Дашборд смены until /me
// answered. Serving the LAST session's identity as SWR fallbackData renders
// the full shell instantly; the background revalidation corrects it if the
// role actually changed (rare; worst case a menu item 403s on click).
const ME_CACHE_KEY = 'me:last';

function readCachedUser(): CurrentUser | undefined {
  try {
    const raw = localStorage.getItem(ME_CACHE_KEY);
    return raw ? (JSON.parse(raw) as CurrentUser) : undefined;
  } catch {
    return undefined;
  }
}

// "A live /me answered this session" — fallbackData makes SWR's `data`
// non-null from the very first render, so the poll-until-fresh interval
// below can't key off data alone; the fetcher flips this instead.
const liveMe = { loaded: false };

export function useCurrentUser(): UseCurrentUserResult {
  const hasJwt = typeof window !== 'undefined' && !!window.localStorage.getItem('jwt');
  const { data, error, isLoading, mutate } = useSWR<CurrentUser>(
    hasJwt ? ME_SWR_KEY : null,
    async () => {
      const me = await fetchCurrentUser();
      liveMe.loaded = true;
      try { localStorage.setItem(ME_CACHE_KEY, JSON.stringify(me)); } catch {}
      return me;
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      fallbackData: hasJwt ? readCachedUser() : undefined,
      // Poll every 15s until a LIVE /me lands (cached identity may be on
      // screen meanwhile) — self-heals after a transient API outage; zero
      // background traffic once fresh.
      refreshInterval: () => (liveMe.loaded ? 0 : 15_000),
    },
  );

  return {
    user: data ?? null,
    isLoading,
    error,
    refresh: () => { void mutate(); },
  };
}
